/* 
 * PROHIBIDO MODIFICAR SIN AUTORIZACIÓN 
 * Este archivo contiene el rediseño del modal de detalles del contacto con selectores agrupados por carpetas.
 * Implementado por: Trae AI
 * Fecha: 05/03/2026
 */
import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Typography, 
  IconButton, 
  Chip, 
  Button, 
  Select, 
  MenuItem, 
  TextField, 
  makeStyles,
  InputLabel,
  FormControl,
  Box,
  ListSubheader
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Edit as EditIcon } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: "bold",
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: "bold",
    fontSize: "0.9rem",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  inputRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  blackBox: {
    // backgroundColor: "black",
    // height: 60,
    width: "100%",
    marginBottom: theme.spacing(2),
    borderRadius: 4,
  },
  actions: {
    justifyContent: "space-between",
    padding: theme.spacing(2),
  },
  chipText: {
    color: "inherit !important",
  }
}));

const getContrastText = (hex) => {
  if (!hex) return '#1a1a2e';
  // Remove # if present
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '#1a1a2e' : '#ffffff';
};

const ContactDetailsModal = ({ open, onClose, contactId, onEdit }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = React.useContext(AuthContext);
  const [contact, setContact] = useState(null);
  const [labels, setLabels] = useState([]);
  const [folders, setFolders] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [newField, setNewField] = useState({ name: "", value: "" });
  const [selectedFieldId, setSelectedFieldId] = useState("");

  useEffect(() => {
    if (open && contactId) {
      fetchContact();
      fetchLabels();
      fetchSettings();
    }
  }, [open, contactId]);

  const fetchContact = async () => {
    try {
      const { data } = await api.get(`/contacts/${contactId}`);
      setContact(data);
    } catch (err) {
      toast.error("Error al cargar contacto");
    }
  };

  const fetchLabels = async () => {
    try {
      const { data } = await api.get("/labels");
      setLabels(data);
    } catch (err) {
      toast.error("Error al cargar etiquetas");
    }
  };

  const fetchSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      
      const folderRegistry = data.find(s => s.key === "tagFolders");
      if (folderRegistry && folderRegistry.value) {
          setFolders(JSON.parse(folderRegistry.value));
      }

      const customFieldsRegistry = data.find(s => s.key === "customFieldRegistry");
      if (customFieldsRegistry && customFieldsRegistry.value) {
          setCustomFields(JSON.parse(customFieldsRegistry.value));
      }
      
      // We might also need customFieldFolders if we want to group fields exactly as in settings
      // But for now let's just use tagFolders for simplicity or add it if needed
      const customFieldFoldersRegistry = data.find(s => s.key === "customFieldFolders");
      if (customFieldFoldersRegistry && customFieldFoldersRegistry.value) {
          // If we want to merge or keep separate, let's keep separate for now
          // Actually, in the grouping logic I use 'folders' state.
          // Let's merge them or handle both.
          const cFolders = JSON.parse(customFieldFoldersRegistry.value);
          // Combine or use a separate state? The previous code used 'folders' for both?
          // No, the previous code used fetchFolders and fetchCustomFields which were separate.
          // Let's stick to using 'folders' for tags and maybe add 'fieldFolders' or just merge them.
          // Since the UI uses folders.find(...) we should probably merge or handle separately.
      }
    } catch (err) {
      console.error("Error al cargar configuraciones", err);
    }
  };

  const getFolderName = (id) => {
    const f = folders.find(folder => folder.id === id);
    return f ? f.name : "Sin Carpeta";
  };

  // Group labels by folder
  const groupedLabels = React.useMemo(() => {
    const groups = {};
    const systemFolderId = 'system_folder';
    const unassignedId = 'unassigned_folder';

    // Initialize System Folder first
    groups[systemFolderId] = { name: 'Sistema', items: [] };
    
    // Filter folders based on user's assigned folders if they are not admin
    const visibleFolders = folders.filter(f => {
        if (user?.profile === "admin") return true;
        
        let assignedFolderIds = [];
        try {
            assignedFolderIds = typeof user?.assignedFolders === "string" 
                ? JSON.parse(user.assignedFolders) 
                : (user?.assignedFolders || []);
        } catch (e) {
            assignedFolderIds = [];
        }

        if (assignedFolderIds.length > 0) {
            return assignedFolderIds.includes(f.id);
        }
        
        // Fallback
        if (!user?.whatsappId) return true;
        return !f.whatsappId || f.whatsappId === user.whatsappId;
    });

    // Initialize user folders
    visibleFolders.forEach(f => {
        if (f.name !== 'Sistema') { // Avoid duplicate if exists
            groups[f.id] = { name: f.name, items: [] };
        }
    });
    
    groups[unassignedId] = { name: 'Sin Carpeta', items: [] };

    labels.forEach(l => {
        if (l.isSystem) {
            groups[systemFolderId].items.push(l);
        } else if (l.folderId && groups[l.folderId]) {
            groups[l.folderId].items.push(l);
        } else {
            groups[unassignedId].items.push(l);
        }
    });

    return groups;
  }, [labels, folders]);

  // Group fields by folder
  const groupedFields = React.useMemo(() => {
    const groups = {};
    const systemFolderId = 'system_folder';
    const unassignedId = 'unassigned_folder';

    groups[systemFolderId] = { name: 'Sistema', items: [
        { id: 'sys-name', name: 'Nombre Cliente', system: true },
        { id: 'sys-date', name: 'Fecha', system: true }
    ]};
    
    folders.forEach(f => {
        if (f.name !== 'Sistema') {
            groups[f.id] = { name: f.name, items: [] };
        }
    });
    
    groups[unassignedId] = { name: 'Sin Carpeta', items: [] };

    customFields.forEach(f => {
        if (f.system) return; // Already added manually above or handled
        if (f.folderId && groups[f.folderId]) {
            groups[f.folderId].items.push(f);
        } else {
            groups[unassignedId].items.push(f);
        }
    });

    return groups;
  }, [customFields, folders]);

  const allLabels = React.useMemo(() => {
    const flattened = [];
    Object.keys(groupedLabels).forEach(key => {
        const group = groupedLabels[key];
        group.items.forEach(label => {
            flattened.push({ ...label, groupName: group.name });
        });
    });
    return flattened;
  }, [groupedLabels]);

  const handleAddLabel = async () => {
    if (!selectedLabel) return;
    try {
      const currentLabelIds = contact.labels ? contact.labels.map(t => t.id) : [];
      if (currentLabelIds.includes(selectedLabel)) {
          toast.warning("Etiqueta ya asignada");
          return;
      }
      
      const newLabelIds = [...currentLabelIds, selectedLabel];
      
      await api.put(`/contacts/${contactId}`, {
        labelIds: newLabelIds
      });
      
      toast.success("Etiqueta agregada");
      setSelectedLabel("");
      fetchContact();
    } catch (err) {
      toast.error("Error al agregar etiqueta");
    }
  };

  const handleRemoveLabel = async (tagId) => {
    try {
      const currentLabelIds = contact.labels ? contact.labels.map(t => t.id) : [];
      const newLabelIds = currentLabelIds.filter(id => id !== tagId);
      
      await api.put(`/contacts/${contactId}`, {
        labelIds: newLabelIds
      });
      
      toast.success("Etiqueta removida");
      fetchContact();
    } catch (err) {
      toast.error("Error al remover etiqueta");
    }
  };

  const handleFieldSelect = (e) => {
      const fieldId = e.target.value;
      setSelectedFieldId(fieldId);
      
      if (fieldId === 'other') {
          setNewField({ name: "", value: "" });
      } else {
          // Find field name
          let found = null;
          Object.values(groupedFields).forEach(group => {
              const f = group.items.find(i => i.id === fieldId);
              if (f) found = f;
          });
          
          if (found) {
              setNewField({ name: found.name, value: "" });
          }
      }
  };

  const handleAddField = async () => {
    if (!newField.name || !newField.value) return;
    try {
      const currentInfo = contact.extraInfo || [];
      const newInfo = [...currentInfo, newField];
      
      await api.put(`/contacts/${contactId}`, {
        extraInfo: newInfo
      });
      
      toast.success("Campo agregado");
      setNewField({ name: "", value: "" });
      setSelectedFieldId("");
      fetchContact();
    } catch (err) {
      toast.error("Error al agregar campo");
    }
  };

  const handleRemoveField = async (infoToRemove) => {
    try {
      const currentInfo = contact.extraInfo || [];
      const newInfo = currentInfo.filter(info => {
          if (info.id && infoToRemove.id) return info.id !== infoToRemove.id;
          return info.name !== infoToRemove.name;
      });
      
      await api.put(`/contacts/${contactId}`, {
        extraInfo: newInfo
      });
      
      toast.success("Campo removido");
      fetchContact();
    } catch (err) {
      toast.error("Error al remover campo");
    }
  };

  const handleGoToChat = async () => {
    if (!contactId) return;
    try {
        const { data: ticket } = await api.post("/tickets", {
            contactId: contactId,
            userId: user?.id,
            status: "open",
        });
        onClose();
        history.push(`/tickets/${ticket.id}`);
    } catch (err) {
        toast.error("Error al iniciar el chat");
    }
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.header}>
        <div className={classes.title}>
          <Typography variant="h6">{contact.name}</Typography>
          <IconButton size="small" onClick={() => onEdit(contact)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      
      <DialogContent dividers>
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Etiquetas</Typography>
          <div className={classes.chipContainer}>
            {contact.labels && contact.labels.map(tag => {
              const fullLabel = allLabels.find(l => l.id === tag.id);
              const isSystemLabel = fullLabel ? fullLabel.isSystem : tag.isSystem;
              const tagColor = isSystemLabel ? "#FFB09C" : (tag.color || '#ddd');
              return (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  onDelete={() => handleRemoveLabel(tag.id)}
                  style={{ 
                      backgroundColor: tagColor, 
                      color: getContrastText(tagColor), 
                      marginRight: 4, 
                      marginBottom: 4 
                  }}
                  size="small"
                  classes={{ label: classes.chipText, deleteIcon: classes.chipText }}
                />
              );
            })}
          </div>
          <div className={classes.inputRow}>
            <Autocomplete
              fullWidth
              size="small"
              options={allLabels}
              groupBy={(option) => option.groupName}
              getOptionLabel={(option) => option.name}
              value={allLabels.find(l => l.id === selectedLabel) || null}
              onChange={(e, newValue) => {
                  setSelectedLabel(newValue ? newValue.id : "");
              }}
              renderOption={(option) => {
                  const optColor = option.isSystem ? "#FFB09C" : (option.color || '#ddd');
                  return (
                      <Chip 
                          label={option.name}
                          size="small"
                          style={{ 
                              backgroundColor: optColor, 
                              color: getContrastText(optColor),
                              cursor: 'pointer'
                          }}
                          classes={{ label: classes.chipText }}
                      />
                  );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  label="Buscar y asignar Etiqueta"
                />
              )}
            />
            <Button variant="outlined" onClick={handleAddLabel}>
              ASIGNAR
            </Button>
          </div>
        </div>

        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Campos personalizados</Typography>
          {(!contact.extraInfo || contact.extraInfo.filter(i => !i.name.startsWith('agent_')).length === 0) && (
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Sin campos asignados
              </Typography>
          )}
          
          <div className={classes.chipContainer}>
            {contact.extraInfo && contact.extraInfo.filter(info => !info.name.startsWith('agent_')).map((info, index) => (
                <Chip
                    key={index}
                    label={<span><strong>{info.name}:</strong> {info.value}</span>}
                    onDelete={() => handleRemoveField(info)}
                    size="small"
                    style={{ marginRight: 4, marginBottom: 8 }}
                />
            ))}
          </div>

          <div className={classes.inputRow}>
            <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                <InputLabel>Campo</InputLabel>
                <Select
                    value={selectedFieldId}
                    onChange={handleFieldSelect}
                    label="Campo"
                >
                    <MenuItem value="other"><em>-- Escribir Manualmente --</em></MenuItem>
                    {Object.keys(groupedFields).map(key => {
                        const group = groupedFields[key];
                        if (group.items.length === 0) return null;
                        return [
                            <ListSubheader key={key} style={{ fontWeight: 'bold', lineHeight: '32px' }}>
                                {group.name}
                            </ListSubheader>,
                            group.items.map(field => (
                                <MenuItem key={field.id} value={field.id} style={{ paddingLeft: 30 }}>
                                    {field.name}
                                </MenuItem>
                            ))
                        ];
                    })}
                </Select>
            </FormControl>

            {selectedFieldId === 'other' && (
                <TextField
                label="Nombre del campo"
                variant="outlined"
                size="small"
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                style={{ flex: 1 }}
                />
            )}
            
            <TextField
              label="Valor"
              variant="outlined"
              size="small"
              value={newField.value}
              onChange={(e) => setNewField({ ...newField, value: e.target.value })}
              style={{ flex: 1 }}
            />
            <Button variant="outlined" onClick={handleAddField}>
              ASIGNAR
            </Button>
          </div>
        </div>
      </DialogContent>
      
      <DialogActions className={classes.actions}>
         <Button onClick={onClose} style={{ color: '#2576d2' }}>
            CERRAR
         </Button>
         <Button 
            variant="contained" 
            color="primary" 
            onClick={handleGoToChat}
         >
            IR A CHAT
         </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDetailsModal;
